import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Wallet, ChevronRight, FileText, PlayCircle, Link as LinkIcon, BookOpen } from "lucide-react";

function PartnerCard({ partner, idx }) {
  const accent = partner.color || "#1B2E7A";
  return (
    <Link
      to={`/financiamiento/${partner.id}`}
      data-testid={`partner-card-${partner.id}`}
      className={`fade-up stagger-${(idx % 6) + 1} group bg-white border border-[#E5E7EB] rounded-md overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col`}
    >
      {/* Brand header */}
      <div
        className="relative aspect-[5/3] flex flex-col justify-between p-5 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}D9 100%)` }}
      >
        <div
          className="absolute inset-0 opacity-15 mix-blend-overlay"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.6), transparent 40%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.4), transparent 50%)",
          }}
        />
        <div className="relative z-10">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/70">Socio de financiamiento</p>
        </div>
        <div className="relative z-10">
          <h3 className="font-heading text-2xl text-white font-medium leading-tight">
            {partner.name}
          </h3>
          {partner.tagline && (
            <p className="text-xs text-white/80 mt-1 italic">{partner.tagline}</p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-sm text-[#374151] leading-relaxed line-clamp-3 flex-1">{partner.summary}</p>
        <div className="flex items-center justify-between mt-4 text-xs">
          <span className="inline-flex items-center gap-1 text-[#737373]">
            <BookOpen className="h-3.5 w-3.5" />
            {partner.resources_count || 0} {partner.resources_count === 1 ? "recurso" : "recursos"}
          </span>
          <span className="inline-flex items-center gap-1 text-[#1B2E7A] font-medium group-hover:gap-2 transition-all">
            Ver detalles <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function FinancingPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/financing-partners");
        setPartners(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="financing-page">
      <div className="mb-10 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2 inline-flex items-center gap-1.5">
          <Wallet className="h-3.5 w-3.5" /> Soluciones de pago
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
          Socios de Financiamiento.
        </h1>
        <p className="text-[#374151] max-w-2xl leading-relaxed">
          Conoce las {partners.length || 8} opciones de financiamiento que ofrecemos en piso. Cada socio
          tiene materiales, condiciones y procesos específicos. Domínalos para asesorar al cliente con
          la opción ideal según su perfil.
        </p>
      </div>

      {loading ? (
        <p className="text-[#374151]">Cargando socios...</p>
      ) : partners.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E7EB] rounded-md">
          <Wallet className="h-10 w-10 text-[#6B7280] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-[#374151]">Aún no hay socios cargados.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {partners.map((p, i) => (
            <PartnerCard key={p.id} partner={p} idx={i} />
          ))}
        </div>
      )}
    </div>
  );
}
